import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactDOM from 'react-dom';
import { getMemberInfo } from '../../services/CommonServices';
import IntlObject from '../Common/IntlObject';
import { useInstance } from '../../contexts/InstanceContext';

const FooterContainer = styled.footer`
  background-color: #1a1a2e;
  color: #e8e8e8;
  margin-top: auto;

  /* Break out of parent container to extend to full viewport width */
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  position: relative;
`;

const FooterTop = styled.div`
  padding: 60px 0 40px;

  @media (max-width: 768px) {
    padding: 40px 0 30px;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  gap: 50px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterLogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  img {
    height: 40px;
    filter: brightness(0) invert(1);
  }

  .logo-separator {
    color: #6a6a6a;
    font-size: 16px;
    font-weight: 300;
  }
`;

const FooterDescription = styled.p`
  font-size: 14px;
  line-height: 1.8;
  color: #a0a0a0;
  margin: 0 0 24px 0;
`;

const FooterTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 24px 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -8px;
    width: 30px;
    height: 2px;
    background: linear-gradient(90deg, #4a90d9, #67b26f);
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  font-size: 14px;
  color: #a0a0a0;

  svg {
    width: 18px;
    height: 18px;
    margin-right: 12px;
    flex-shrink: 0;
    margin-top: 2px;
    color: #4a90d9;
  }

  span {
    line-height: 1.5;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const SocialLink = styled.a`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  text-decoration: none;

  svg {
    width: 18px;
    height: 18px;
    color: #a0a0a0;
    transition: color 0.3s ease;
  }

  &:hover {
    background: linear-gradient(135deg, #4a90d9, #67b26f);
    transform: translateY(-3px);

    svg {
      color: #ffffff;
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 20px 0;
`;

const FooterBottomContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 576px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-size: 13px;
  color: #6a6a6a;
  margin: 0;
`;

const FooterBottomLinks = styled.div`
  display: flex;
  gap: 20px;

  button {
    font-size: 13px;
    color: #6a6a6a;
    text-decoration: none;
    transition: color 0.3s ease;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;

    &:hover {
      color: #ffffff;
    }
  }
`;

// Policy Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  max-width: 700px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 24px 30px;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 1;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    color: #1a1a2e;
    font-weight: 600;
  }
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #666;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 30px;
  color: #333;
  line-height: 1.8;
  font-size: 14px;

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 24px 0 12px 0;
    color: #1a1a2e;
  }

  p {
    margin: 0 0 16px 0;
  }

  ul {
    margin: 0 0 16px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }
`;

// Icons
const PhoneIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const EmailIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const LocationIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const BuildingIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const BlogIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24">
    <path d="M19.5 3h-15A1.5 1.5 0 003 4.5v15A1.5 1.5 0 004.5 21h15a1.5 1.5 0 001.5-1.5v-15A1.5 1.5 0 0019.5 3zM8 17H6v-2h2v2zm0-4H6v-2h2v2zm0-4H6V7h2v2zm10 8h-8v-2h8v2zm0-4h-8v-2h8v2zm0-4h-8V7h8v2z" />
  </svg>
);

const FacebookIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
  </svg>
);

const InstagramIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const YouTubeIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

// Policy Modal Component
const PolicyModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h2>{title}</h2>
          <ModalCloseButton onClick={onClose}>&times;</ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          {children}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

const FooterType01 = ({ site, intl }) => {
  const [memberInfo, setMemberInfo] = useState({});
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const currentYear = new Date().getFullYear();
  const { memberId } = useInstance();

  useEffect(() => {
    if (!memberId) return;
    getMemberInfo(memberId)
      .then((response) => {
        setMemberInfo(response.data.memberInfo || {});
      })
      .catch((error) => {
        console.error('Error fetching member info: ', error);
      });
  }, [memberId]);

  return (
    <FooterContainer>
      <FooterTop>
        <FooterContent>
          {/* Company Info Section */}
          <FooterSection>
            <FooterLogoWrapper>
              {memberInfo.site_logo && (
                <>
                  <img src={memberInfo.site_logo} alt="Site Logo" />
                  <span className="logo-separator">×</span>
                </>
              )}
              <img
                src="https://bubanc.s3.amazonaws.com/bms/common/bubanc.svg"
                alt="Bubanc"
              />
            </FooterLogoWrapper>
            <FooterDescription>
              {memberInfo.description || <IntlObject keycode="footer.defaultDescription" returnType="span" />}
            </FooterDescription>
            <SocialLinks>
              {/* Blog - 항상 표시 */}
              <SocialLink as={Link} to="/blog" aria-label="Blog">
                <BlogIcon />
              </SocialLink>

              {/* SNS - site에서 조건부 렌더링 */}
              {site?.facebook && (
                <SocialLink href={site.facebook} target="_blank" rel="noreferrer" aria-label="Facebook">
                  <FacebookIcon />
                </SocialLink>
              )}
              {site?.youtube && (
                <SocialLink href={site.youtube} target="_blank" rel="noreferrer" aria-label="YouTube">
                  <YouTubeIcon />
                </SocialLink>
              )}
              {site?.instagram && (
                <SocialLink href={site.instagram} target="_blank" rel="noreferrer" aria-label="Instagram">
                  <InstagramIcon />
                </SocialLink>
              )}
            </SocialLinks>
          </FooterSection>

          {/* Contact Section */}
          <FooterSection>
            <FooterTitle>
              <IntlObject keycode="footer.contactUs" returnType="span" />
            </FooterTitle>

            {memberInfo.phone && (
              <ContactItem>
                <PhoneIcon />
                <span>{memberInfo.phone}</span>
              </ContactItem>
            )}

            {memberInfo.email && (
              <ContactItem>
                <EmailIcon />
                <span>{memberInfo.email}</span>
              </ContactItem>
            )}

            {memberInfo.address && (
              <ContactItem>
                <LocationIcon />
                <span>{memberInfo.address}</span>
              </ContactItem>
            )}

            {/* Fallback if no member info */}
            {!memberInfo.phone && !memberInfo.email && !memberInfo.address && (
              <>
                <ContactItem>
                  <PhoneIcon />
                  <span>201.201.9883</span>
                </ContactItem>
                <ContactItem>
                  <EmailIcon />
                  <span>info@theadjoa.com</span>
                </ContactItem>
                <ContactItem>
                  <LocationIcon />
                  <span>2160 N Central Rd #112<br />Fort Lee, NJ 07024</span>
                </ContactItem>
              </>
            )}
          </FooterSection>

          {/* Company Info Section */}
          <FooterSection>
            <FooterTitle>
              <IntlObject keycode="footer.companyInfo" returnType="span" />
            </FooterTitle>

            {memberInfo.company_name && (
              <ContactItem>
                <BuildingIcon />
                <span>{memberInfo.company_name}</span>
              </ContactItem>
            )}

            {memberInfo.company_phone && (
              <ContactItem>
                <PhoneIcon />
                <span>{memberInfo.company_phone}</span>
              </ContactItem>
            )}

            {memberInfo.company_address && (
              <ContactItem>
                <LocationIcon />
                <span>{memberInfo.company_address}</span>
              </ContactItem>
            )}

            {/* Hide section completely if no company info */}
            {!memberInfo.company_name && !memberInfo.company_phone && !memberInfo.company_address && (
              <ContactItem style={{ color: '#6a6a6a', fontStyle: 'italic' }}>
                <span><IntlObject keycode="footer.noCompanyInfo" returnType="span" /></span>
              </ContactItem>
            )}
          </FooterSection>
        </FooterContent>
      </FooterTop>

      <FooterBottom>
        <FooterBottomContent>
          <Copyright>
            © {currentYear} {memberInfo.companyName || 'Bubanc'}. <IntlObject keycode="footer.copyright" returnType="span" />
          </Copyright>
          <FooterBottomLinks>
            <button type="button" onClick={() => setShowPrivacyModal(true)}>
              <IntlObject keycode="footer.privacy" returnType="span" />
            </button>
            <button type="button" onClick={() => setShowTermsModal(true)}>
              <IntlObject keycode="footer.terms" returnType="span" />
            </button>
          </FooterBottomLinks>
        </FooterBottomContent>
      </FooterBottom>

      {/* Privacy Policy Modal */}
      <PolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title={intl.formatMessage({ id: 'footer.privacy', defaultMessage: 'Privacy Policy' })}
      >
        <IntlObject keycode="footer.privacyContent" returnType="div" />
      </PolicyModal>

      {/* Terms of Service Modal */}
      <PolicyModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title={intl.formatMessage({ id: 'footer.terms', defaultMessage: 'Terms of Service' })}
      >
        <IntlObject keycode="footer.termsContent" returnType="div" />
      </PolicyModal>
    </FooterContainer>
  );
};

FooterType01.propTypes = {
  site: PropTypes.object,
  intl: PropTypes.object.isRequired,
};

FooterType01.defaultProps = {
  site: null,
};

export default injectIntl(FooterType01);
